import { Link } from 'react-router-dom';
import { Typography, Divider } from 'antd';
import Footer from '../../Routes/Footer';
import PublicHeader from '../../Routes/PublicHeader';
import { useNodeInfo } from '../../hooks/use_node_info';
import { useNodeSettings } from '../../hooks/use_node_settings';

const { Title, Paragraph, Text } = Typography;

const LAST_UPDATED = '2026-09-22';

function PrivacyPolicy() {
    const { featureTrustPage } = useNodeInfo();
    const { legalEntityName, platformLegalEntityName } = useNodeSettings();
    return (
      <>
        <PublicHeader />
        <div style={{ maxWidth: 720, margin: '0 auto', padding: '32px 16px 64px' }}>
            <Title level={3}>Privacy Policy</Title>
            <Paragraph type="secondary">Last updated: {LAST_UPDATED}</Paragraph>

            <Paragraph>
                This node is operated by {legalEntityName || platformLegalEntityName}
                {' '}("we", "us").
                {legalEntityName
                    ? <> {legalEntityName} is the <Text strong>data controller</Text> for
                      the personal data described below: it decides why and how your data
                      is used on this node. {platformLegalEntityName} builds and runs the
                      underlying Vellby platform — hosting, software, and AI features —
                      as {legalEntityName}'s <Text strong>data processor</Text>, under a
                      data processing agreement between the two: it processes your data
                      only on {legalEntityName}'s instructions and does not use it for
                      its own purposes.</>
                    : <> As the platform provider, {platformLegalEntityName} is also the
                      data controller for this node, since no separate node-owner
                      organization has been designated to run it.</>} This
                policy explains what data is collected, why, and how it's handled. See
                also {featureTrustPage && (
                    <>our <Link to="/trust">Trust page</Link> for a short summary of what
                    we don't do, and </>
                )}our <Link to="/terms">Terms of Use</Link> for the rules of using
                Vellby.
            </Paragraph>

            <Title level={4}>Data we collect</Title>
            <ul>
                <li><Text strong>Account &amp; profile data</Text> — email, username, display name, and role/group membership, managed through our Keycloak login system.</li>
                <li><Text strong>Content you create</Text> — entities (contexts, people, relationships, apps), messages, and connections you add to Vellby.</li>
                <li><Text strong>Embeddings</Text> — vector embeddings generated from your content to power search.</li>
                <li><Text strong>Agent tokens</Text> — if you connect your own AI agent, a hashed copy of each token you create and a record of the changes it makes on your behalf.</li>
                <li><Text strong>Billing information</Text> — if you subscribe to a paid plan, payment details are handled directly by Stripe; we never see or store your card number.</li>
                <li><Text strong>Federation data</Text> — if you interact with an entity hosted on a different Vellby server, a limited, signed exchange of identifiers and messages relevant to that interaction is shared with that server.</li>
            </ul>

            <Title level={4}>What we don't do</Title>
            <ul>
                <li>No advertising or analytics cookies, and no third-party tracking pixels.</li>
                <li>We don't sell or rent your personal data.</li>
                <li>The only cookie in use is Keycloak's own login-session cookie — strictly necessary to keep you signed in.</li>
                <li>Local browser storage on your device holds only a cached copy of your profile fields (for fast page loads) and your light/dark theme preference — nothing used for tracking or advertising.</li>
            </ul>
            <Paragraph>
                One exception worth naming plainly: when someone loads the app without
                being logged in, we record which page they landed on, with no cookie and
                no identifier attached, purely so we can see which unauthenticated pages
                get traffic.
            </Paragraph>

            <Title level={4}>Where data is processed</Title>
            {legalEntityName && (
                <Paragraph type="secondary">
                    {platformLegalEntityName} engages the services below as
                    {' '}{legalEntityName}'s processor — {legalEntityName} remains the
                    data controller for your data throughout.
                </Paragraph>
            )}
            <ul>
                <li><Text strong>Hosting</Text> — Google Cloud Platform, in the London (europe-west2) region. Your entities, messages, profile, and embeddings are stored here.</li>
                <li><Text strong>AI processing</Text> — Google Gemini, for embeddings and AI-composed hangout breakout rooms. Gemini API calls may be processed outside the EEA; we're working through an appropriate international-transfer safeguard for this and will update this policy once it's in place.</li>
                <li><Text strong>Payments</Text> — Stripe, for paid subscriptions.</li>
                <li><Text strong>Authentication</Text> — Keycloak, which we self-host on our own infrastructure rather than a third-party provider.</li>
            </ul>
            <Paragraph>
                We're moving AI processing in-house. Embeddings run on
                Google Gemini today, but we're migrating to language models we host
                ourselves, on the same infrastructure that already holds the rest of
                your data. Once that migration is complete, your content will no longer
                be sent to a third-party AI provider, and the international transfer
                described above will no longer apply to it. We'll update this policy,
                and the date at the top of it, when that happens.
            </Paragraph>

            <Title level={4}>Data retention</Title>
            <Paragraph>
                We retain your data for as long as your account is active. You can
                permanently delete your account at any time from Account settings —
                this removes your login and cascades deletion of your profile, the
                entities you own, and related data.
            </Paragraph>

            <Title level={4}>Consents</Title>
            <Paragraph>
                When you sign in, this node asks you to respond to its consents — some
                required, some optional. Each response is recorded along with the exact
                version of the wording you saw, and you're asked again if the wording
                changes.{legalEntityName && <> Because this node has its own operator,
                one of those required consents spells out the controller/processor split
                described above, and updates automatically whenever {legalEntityName}
                {' '}changes.</>} The current list is on the <Link to="/consents">Consents</Link>{' '}
                page. Deleting your account deletes your consent records.
            </Paragraph>

            <Title level={4}>Your rights</Title>
            <Paragraph>
                Depending on where you live, you may have rights to access, correct,
                delete, or export your data, and to object to certain processing.
                Account deletion is self-service (see above); for anything else, contact
                us at <Text code>info@vellby.com</Text>.
            </Paragraph>

            <Title level={4}>Security</Title>
            <Paragraph>
                All traffic to Vellby is encrypted in transit (TLS). Authentication is
                handled by Keycloak, and data at rest is encrypted using
                GCP-managed disk encryption.
            </Paragraph>

            <Divider />
            <Paragraph type="secondary">
                We'll update the date at the top of this page whenever this policy
                changes. Questions? Reach us at <Text code>info@vellby.com</Text>.
            </Paragraph>
            <Footer />
        </div>
      </>
    );
}

export default PrivacyPolicy;
